import { DOCUMENT } from '@angular/common';
import { computed, inject, Injectable, signal } from '@angular/core';

/**
 * Tracks the open dialogs in the order they were opened, so only the topmost one handles Escape
 * and Tab, lower ones can be made inert, and page scroll is locked while any dialog is open.
 */
@Injectable({ providedIn: 'root' })
export class DialogStackService {
  private readonly doc = inject(DOCUMENT);
  private readonly ids = signal<string[]>([]);
  private previousOverflow = '';

  /** Ids of the open dialogs, oldest first. */
  readonly stack = this.ids.asReadonly();

  /** Whether at least one dialog is open. */
  readonly hasOpenDialog = computed(() => this.ids().length > 0);

  /**
   * Registers an opened dialog.
   *
   * @param id Unique id of the dialog.
   * @returns Its depth: `0` when it is the only open dialog, `1` when opened over another, and so on.
   */
  push(id: string): number {
    const depth = this.ids().length;
    if (depth === 0) {
      this.previousOverflow = this.doc.body.style.overflow;
      this.doc.body.style.overflow = 'hidden';
    }
    this.ids.update((list) => [...list, id]);
    return depth;
  }

  /** Unregisters a closed dialog; restores page scroll when it was the last one. */
  remove(id: string): void {
    if (!this.ids().includes(id)) return;
    this.ids.update((list) => list.filter((item) => item !== id));
    if (this.ids().length === 0) this.doc.body.style.overflow = this.previousOverflow;
  }

  /** Whether `id` is the most recently opened dialog. */
  isTop(id: string): boolean {
    const list = this.ids();
    return list.length > 0 && list[list.length - 1] === id;
  }

  /** Whether another dialog is open above `id` (so `id` must be inert). */
  isCovered(id: string): boolean {
    const list = this.ids();
    const index = list.indexOf(id);
    return index >= 0 && index < list.length - 1;
  }
}
